import { useState, useEffect, useRef } from "react";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const METHOD_COLORS = {
  GET: "#16a34a", POST: "#d97706", PUT: "#2563eb", PATCH: "#7c3aed", DELETE: "#dc2626",
};

const DEFAULT_COLLECTIONS = [
  { id: 1, name: "JSONPlaceholder", requests: [
    { id: 11, method: "GET", name: "Get posts", url: "https://jsonplaceholder.typicode.com/posts" },
    { id: 12, method: "POST", name: "Create post", url: "https://jsonplaceholder.typicode.com/posts" },
    { id: 13, method: "GET", name: "Get users", url: "https://jsonplaceholder.typicode.com/users" },
  ]},
  { id: 2, name: "HTTPBin", requests: [
    { id: 21, method: "GET", name: "Get IP", url: "https://httpbin.org/ip" },
    { id: 22, method: "POST", name: "Post echo", url: "https://httpbin.org/post" },
  ]},
];

function Badge({ method }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, fontFamily: "monospace",
      color: METHOD_COLORS[method] || "#555",
      background: (METHOD_COLORS[method] || "#555") + "18",
      border: `1px solid ${(METHOD_COLORS[method] || "#555")}44`,
      borderRadius: 3, padding: "1px 5px", letterSpacing: 0.5, flexShrink: 0,
    }}>{method}</span>
  );
}

function KeyValueEditor({ rows, onChange, placeholder = "key" }) {
  const update = (i, field, val) => onChange(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const add = () => onChange([...rows, { key: "", value: "", enabled: true }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((row, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={row.enabled} onChange={e => update(i, "enabled", e.target.checked)} style={{ width: 16, height: 16, accentColor: "#16a34a", flexShrink: 0 }} />
          <input value={row.key} onChange={e => update(i, "key", e.target.value)} placeholder={placeholder} style={inputStyle} />
          <input value={row.value} onChange={e => update(i, "value", e.target.value)} placeholder="value" style={{ ...inputStyle, flex: 2 }} />
          <button onClick={() => remove(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 16, padding: "0 4px", flexShrink: 0 }}>✕</button>
        </div>
      ))}
      <button onClick={add} style={{ alignSelf: "flex-start", background: "none", border: "1px dashed #ccc", borderRadius: 4, padding: "4px 12px", fontSize: 11, cursor: "pointer", color: "#888", fontFamily: "monospace" }}>+ add</button>
    </div>
  );
}

const inputStyle = {
  flex: 1, background: "#fff", border: "1px solid #d1d5db", borderRadius: 6,
  padding: "8px 10px", fontSize: 13, fontFamily: "monospace", color: "#111", outline: "none",
};

const btnStyle = (color = "#16a34a", disabled = false) => ({
  background: disabled ? "#e5e7eb" : color,
  border: "none", borderRadius: 6, padding: "10px 20px",
  fontSize: 13, fontWeight: 700, fontFamily: "monospace",
  color: disabled ? "#9ca3af" : "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
  flexShrink: 0,
});

const statusColor = (s) => {
  if (!s) return "#888";
  if (s < 300) return "#16a34a";
  if (s < 400) return "#d97706";
  return "#dc2626";
};

function WebhookTester() {
  const [token, setToken] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [listening, setListening] = useState(false);
  const pollRef = useRef(null);

  const createEndpoint = async () => {
    setLoading(true); setRequests([]); setSelected(null);
    try {
      const res = await fetch("https://webhook.site/token", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ default_status: 200, default_content: "OK", default_content_type: "text/plain" }),
      });
      const data = await res.json();
      setToken(data.uuid); setWebhookUrl(`https://webhook.site/${data.uuid}`); setListening(true);
    } catch { alert("Failed to create endpoint. Try again."); }
    setLoading(false);
  };

  const fetchRequests = async (uuid) => {
    try {
      const res = await fetch(`https://webhook.site/token/${uuid}/requests?sorting=newest`);
      const data = await res.json();
      setRequests(data.data || []);
    } catch {}
  };

  useEffect(() => {
    if (!token || !listening) return;
    fetchRequests(token);
    pollRef.current = setInterval(() => fetchRequests(token), 3000);
    return () => clearInterval(pollRef.current);
  }, [token, listening]);

  const copy = () => { navigator.clipboard.writeText(webhookUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const reset = () => { clearInterval(pollRef.current); setToken(null); setWebhookUrl(""); setRequests([]); setSelected(null); setListening(false); };
  const sel = requests.find(r => r.uuid === selected);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 6 }}>🪝 Webhook Tester</div>
        <div style={{ fontSize: 11, color: "#555", lineHeight: 1.6 }}>Generate a live URL. Send any HTTP request to it. See the payload here in real time.</div>
      </div>
      {!token ? (
        <button onClick={createEndpoint} disabled={loading} style={{ ...btnStyle("#d97706", loading), width: "100%", padding: 14, fontSize: 14 }}>
          {loading ? "Creating..." : "⚡ Generate Webhook URL"}
        </button>
      ) : (
        <>
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 10, color: "#d97706", marginBottom: 6, letterSpacing: 1, fontWeight: 600 }}>YOUR WEBHOOK URL</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <code style={{ flex: 1, fontSize: 11, color: "#111", wordBreak: "break-all", background: "#fff", padding: "6px 10px", borderRadius: 4, border: "1px solid #e5e7eb" }}>{webhookUrl}</code>
              <button onClick={copy} style={{ ...btnStyle(copied ? "#16a34a" : "#6b7280"), padding: "8px 14px", fontSize: 11 }}>{copied ? "✓ Copied" : "Copy"}</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#16a34a", display: "inline-block", animation: "pulse 1.5s infinite" }} />
              <span style={{ fontSize: 10, color: "#16a34a", fontWeight: 600 }}>Listening...</span>
              <button onClick={reset} style={{ marginLeft: "auto", background: "none", border: "1px solid #d1d5db", borderRadius: 4, padding: "3px 10px", fontSize: 10, color: "#666", cursor: "pointer", fontFamily: "monospace" }}>Reset</button>
            </div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f9fafb" }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#111" }}>Incoming Requests</span>
              <span style={{ fontSize: 10, color: "#9ca3af" }}>{requests.length} received</span>
            </div>
            {requests.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 12 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                No requests yet — send something to your URL!
              </div>
            ) : requests.map(req => (
              <div key={req.uuid} onClick={() => setSelected(selected === req.uuid ? null : req.uuid)}
                style={{ padding: "10px 14px", borderBottom: "1px solid #f3f4f6", cursor: "pointer", background: selected === req.uuid ? "#f0fdf4" : "#fff", display: "flex", alignItems: "center", gap: 8 }}>
                <Badge method={req.method} />
                <span style={{ fontSize: 11, color: "#555", flex: 1 }}>{new Date(req.created_at).toLocaleTimeString()}</span>
                <span style={{ fontSize: 10, color: "#16a34a", fontWeight: 600 }}>200</span>
                <span style={{ fontSize: 11, color: "#aaa" }}>{selected === req.uuid ? "▲" : "▼"}</span>
              </div>
            ))}
          </div>
          {sel && (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#111" }}>Request Detail</div>
              <div>
                <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 4, letterSpacing: 1 }}>METHOD & URL</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}><Badge method={sel.method} /><code style={{ fontSize: 11, color: "#555" }}>{sel.url}</code></div>
              </div>
              {sel.headers && Object.keys(sel.headers).length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 6, letterSpacing: 1 }}>HEADERS</div>
                  <div style={{ background: "#f9fafb", borderRadius: 6, padding: 10, display: "flex", flexDirection: "column", gap: 4, border: "1px solid #e5e7eb" }}>
                    {Object.entries(sel.headers).map(([k, v]) => (
                      <div key={k} style={{ display: "flex", gap: 8, fontSize: 11 }}>
                        <span style={{ color: "#7c3aed", minWidth: 140, flexShrink: 0 }}>{k}</span>
                        <span style={{ color: "#111", wordBreak: "break-all" }}>{Array.isArray(v) ? v.join(", ") : v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {sel.content && (
                <div>
                  <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 6, letterSpacing: 1 }}>BODY</div>
                  <pre style={{ background: "#f9fafb", borderRadius: 6, padding: 10, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#111", margin: 0, maxHeight: 200, overflowY: "auto", lineHeight: 1.6, border: "1px solid #e5e7eb" }}>
                    {(() => { try { return JSON.stringify(JSON.parse(sel.content), null, 2); } catch { return sel.content; } })()}
                  </pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ApiClient() {
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("https://jsonplaceholder.typicode.com/posts/1");
  const [tab, setTab] = useState("params");
  const [mainTab, setMainTab] = useState("request");
  const [params, setParams] = useState([{ key: "", value: "", enabled: true }]);
  const [headers, setHeaders] = useState([{ key: "Content-Type", value: "application/json", enabled: true }]);
  const [body, setBody] = useState('{\n  "title": "foo",\n  "body": "bar",\n  "userId": 1\n}');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiMode, setAiMode] = useState("explain");
  const [aiInput, setAiInput] = useState("");
  const [aiOutput, setAiOutput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [collections] = useState(DEFAULT_COLLECTIONS);
  const [expandedCols, setExpandedCols] = useState({ 1: true, 2: false });

  const buildUrl = () => {
    const enabled = params.filter(p => p.enabled && p.key);
    if (!enabled.length) return url;
    const qs = enabled.map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join("&");
    return url.includes("?") ? `${url}&${qs}` : `${url}?${qs}`;
  };

  const sendRequest = async () => {
    setLoading(true); setResponse(null);
    const start = Date.now();
    try {
      const hdrs = {};
      headers.filter(h => h.enabled && h.key).forEach(h => { hdrs[h.key] = h.value; });
      const opts = { method, headers: hdrs };
      if (!["GET", "DELETE"].includes(method) && body) opts.body = body;
      const res = await fetch(buildUrl(), opts);
      const text = await res.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch {}
      setResponse({ status: res.status, statusText: res.statusText, time: Date.now() - start, size: new Blob([text]).size, body: parsed ? JSON.stringify(parsed, null, 2) : text, ok: res.ok });
      setMainTab("response");
    } catch (e) {
      setResponse({ error: e.message, time: Date.now() - start, ok: false });
      setMainTab("response");
    }
    setLoading(false);
  };

  const callAI = async () => {
    setAiLoading(true); setAiOutput("");
    let prompt = "";
    if (aiMode === "explain" && response) prompt = `Explain this HTTP response:\nRequest: ${method} ${url}\nStatus: ${response.status} ${response.statusText}\nBody:\n${response.body}\nBe concise.`;
    else if (aiMode === "generate") prompt = `Generate the best HTTP request for: "${aiInput}"\nRespond with Method, URL, Headers, Body. Be concise.`;
    else if (aiMode === "debug" && response) prompt = `Debug this API error:\nRequest: ${method} ${url}\nStatus: ${response.status}\nBody: ${response.body || response.error}\nGive 2-3 fix steps.`;
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, messages: [{ role: "user", content: prompt }] }),
      });
      const data = await res.json();
      setAiOutput(data.content?.find(b => b.type === "text")?.text || "No response.");
    } catch (e) { setAiOutput("Error: " + e.message); }
    setAiLoading(false);
  };

  const navTabs = [
    { id: "request", label: " Request" },
    { id: "response", label: `${response ? (response.ok ? "✅" : "❌") : ""} Response` },
    { id: "collections", label: " Saved" },
    { id: "ai", label: " AI" },
    { id: "webhook", label: " Webhook" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", color: "#111", fontFamily: "monospace", fontSize: 13 }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>          
          <span style={{ fontWeight: 700, fontSize: 15, color: "#111", letterSpacing: 1 }}>irathpost</span>          
        </div>
        <span style={{ fontSize: 10, color: "#9ca3af" }}>by ishan</span>
      </div>

      {mainTab !== "webhook" && (
        <div style={{ padding: "12px 16px", background: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={method} onChange={e => setMethod(e.target.value)}
            style={{ background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 8px", fontSize: 12, fontWeight: 700, fontFamily: "monospace", color: METHOD_COLORS[method], cursor: "pointer", outline: "none", flexShrink: 0 }}>
            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && sendRequest()}
            placeholder="Enter URL..." style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
          <button onClick={sendRequest} disabled={loading} style={btnStyle("#16a34a", loading)}>
            {loading ? "..." : "Send"}
          </button>
        </div>
      )}

      <div style={{ display: "flex", background: "#fff", borderBottom: "1px solid #e5e7eb", overflowX: "auto" }}>
        {navTabs.map(t => (
          <button key={t.id} onClick={() => setMainTab(t.id)}
            style={{ flex: 1, minWidth: 70, padding: "10px 6px", background: "none", border: "none", borderBottom: mainTab === t.id ? "2px solid #16a34a" : "2px solid transparent", fontSize: 10, cursor: "pointer", color: mainTab === t.id ? "#111" : "#9ca3af", fontFamily: "monospace", whiteSpace: "nowrap", fontWeight: 700 }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: 16 }}>
        {mainTab === "request" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 8 }}>
              {["params", "headers", "body"].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  style={{ padding: "7px 14px", background: tab === t ? "#f0fdf4" : "#fff", border: `1px solid ${tab === t ? "#16a34a" : "#d1d5db"}`, borderRadius: 6, fontSize: 11, cursor: "pointer", color: tab === t ? "#16a34a" : "#6b7280", fontFamily: "monospace", fontWeight: tab === t ? 600 : 400 }}>
                  {t}
                </button>
              ))}
            </div>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
              {tab === "params" && <KeyValueEditor rows={params} onChange={setParams} placeholder="param" />}
              {tab === "headers" && <KeyValueEditor rows={headers} onChange={setHeaders} placeholder="header" />}
              {tab === "body" && <textarea value={body} onChange={e => setBody(e.target.value)} style={{ ...inputStyle, width: "100%", minHeight: 120, resize: "vertical", boxSizing: "border-box" }} />}
            </div>
            <button onClick={sendRequest} disabled={loading} style={{ ...btnStyle("#16a34a", loading), width: "100%", padding: 14, fontSize: 14 }}>
              {loading ? "Sending..." : "⚡ Send Request"}
            </button>
          </div>
        )}

        {mainTab === "response" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {!response && !loading && <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}><div style={{ fontSize: 32, marginBottom: 8 }}>📥</div><div>Send a request to see the response</div></div>}
            {loading && <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}><div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div><div>Sending request...</div></div>}
            {response && !loading && (
              <>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: statusColor(response.status) }}>{response.error ? "Error" : `${response.status} ${response.statusText}`}</span>
                  {!response.error && <>
                    <span style={{ fontSize: 11, background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 4, padding: "2px 8px", color: "#555" }}>{response.time}ms</span>
                    <span style={{ fontSize: 11, background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 4, padding: "2px 8px", color: "#555" }}>{response.size}B</span>
                  </>}
                </div>
                {response.error && <div style={{ color: "#dc2626", fontSize: 13 }}>{response.error}</div>}
                {response.body && <pre style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, fontSize: 11, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.7, maxHeight: 400, overflowY: "auto", color: "#111" }}>{response.body}</pre>}
              </>
            )}
          </div>
        )}

        {mainTab === "collections" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {collections.map(col => (
              <div key={col.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
                <div onClick={() => setExpandedCols(e => ({ ...e, [col.id]: !e[col.id] }))}
                  style={{ padding: "12px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, background: "#f9fafb" }}>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>{expandedCols[col.id] ? "▼" : "▶"}</span>
                  <span style={{ fontWeight: 600, color: "#111" }}>{col.name}</span>
                  <span style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af" }}>{col.requests.length} requests</span>
                </div>
                {expandedCols[col.id] && col.requests.map(req => (
                  <div key={req.id} onClick={() => { setMethod(req.method); setUrl(req.url); setResponse(null); setMainTab("request"); }}
                    style={{ padding: "10px 14px 10px 28px", borderTop: "1px solid #f3f4f6", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                    <Badge method={req.method} />
                    <span style={{ fontSize: 12, color: "#374151" }}>{req.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {mainTab === "ai" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 8 }}>
              {[{ id: "explain", label: "Explain" }, { id: "generate", label: "Generate" }, { id: "debug", label: "Debug" }].map(m => (
                <button key={m.id} onClick={() => { setAiMode(m.id); setAiOutput(""); }}
                  style={{ flex: 1, padding: "8px 0", fontSize: 12, border: `1px solid ${aiMode === m.id ? "#7c3aed" : "#d1d5db"}`, borderRadius: 6, background: aiMode === m.id ? "#ede9fe" : "#fff", color: aiMode === m.id ? "#7c3aed" : "#6b7280", cursor: "pointer", fontFamily: "monospace", fontWeight: aiMode === m.id ? 600 : 400 }}>
                  {m.label}
                </button>
              ))}
            </div>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>
              {aiMode === "explain" && "Send a request first, then tap Run to get a plain-English explanation of the response."}
              {aiMode === "debug" && "Send a request that returns an error, then tap Run to get step-by-step fix instructions."}
              {aiMode === "generate" && <textarea value={aiInput} onChange={e => setAiInput(e.target.value)} placeholder="Describe the API call you want to make..." style={{ ...inputStyle, width: "100%", minHeight: 80, resize: "none", boxSizing: "border-box", fontSize: 13 }} />}
            </div>
            <button onClick={callAI} disabled={aiLoading || (aiMode !== "generate" && !response)}
              style={{ ...btnStyle("#7c3aed", aiLoading || (aiMode !== "generate" && !response)), width: "100%", padding: 14, fontSize: 14 }}>
              {aiLoading ? "✦ thinking..." : "✦ Run AI"}
            </button>
            {aiOutput && (
              <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: 14 }}>
                <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.7, color: "#111", margin: 0 }}>{aiOutput}</pre>
              </div>
            )}
          </div>
        )}

        {mainTab === "webhook" && <WebhookTester />}
      </div>

      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; background: #f9fafb; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: #f3f4f6; }
        ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }
        input, textarea, select { -webkit-appearance: none; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>
    </div>
  );
}